import React, { FC, ReactNode, useMemo } from 'react';
import { Paper, useTheme } from '@mui/material';
import moment from 'moment-timezone';
import { nanoid } from 'nanoid';

import { Tooltip } from 'src/shared/components/Tooltip';
import { useTranslate } from 'src/shared/providers';

import './DailyCalendar.styles.scss';

type VariantType = 'default' | 'success' | 'error' | 'warning' | 'info' | 'muted' | 'danger' | 'pending';

export type DailyEventType = {
  type: VariantType;
  time: string | null;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  unpublished?: boolean;
  tooltip?: ReactNode;
  onClick?: () => void;
};

interface Props {
  date: moment.Moment;
  events?: DailyEventType[];
  className?: string;
}

export const DailyCalendar: FC<Props> = ({ date, events, className = '' }) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const hasUnpublished = useMemo(() => events?.some((event) => event.unpublished), [events]);
  const unpublishedClass = hasUnpublished ? 'unpublished' : '';
  const { mode } = theme.palette;

  return (
    <Paper className={`daily-calendar mb-2 p-2 ${unpublishedClass} ${className} ${mode}`}>
      <div className="text-center">
        <p className={`day mb-0 text-capitalize ${mode}`}>{date.format('ddd')}</p>
        <p className={`day day-number mb-3 ${mode}`}>{date.format('D')}</p>
      </div>
      {events && (
        <div className="h-75 overflow-auto">
          {events.map(({ title, description, type, icon, tooltip, unpublished, onClick }) => {
            if (unpublished) {
              return null;
            }

            const cursorClass = onClick ? 'cursor-pointer' : '';

            return (
              <Tooltip key={nanoid()} title={tooltip} showIcon={false}>
                <div className={`daily-calendar-event ${type} ${cursorClass} ${mode}`} onClick={onClick}>
                  <div className="title">{title}</div>
                  {description && <div className="description">{description}</div>}
                  {icon && <div className="daily-calendar-event-icon border">{icon}</div>}
                </div>
              </Tooltip>
            );
          })}
          {hasUnpublished && (
            <div className="daily-not-published">
              <div className="shadow-sm">{t('calendar.notPublished')}</div>
            </div>
          )}
        </div>
      )}
    </Paper>
  );
};
